/**
 * Transforms the input JSON Schema form by making the following changes:
 *   1. Removes `unstract_key` from the `required` array (if present).
 *   2. Adds `use_platform_provided_unstract_key` to the `required` array (if not already present).
 *   3. Inserts a new boolean field `use_platform_provided_unstract_key` immediately above `unstract_key` in the properties.
 *   4. Marks `unstract_key` as `readOnly` by default.
 *   5. Adds or overrides the `use_platform_provided_unstract_key` dependency to toggle the `readOnly` state of `unstract_key`.
 *   6. Returns the original JSON schema without changes if `unstract_key` is not present in `properties`.
 *
 * @param {Object} inputSchema - The original JSON schema to transform.
 * @return {Object} - A new, modified JSON schema with the required changes.
 */
function transformLlmWhispererJsonSchema(inputSchema) {
  /**
   * Step 1: Deep-clone the input schema to avoid mutating the original object.
   */
  const schema = JSON.parse(JSON.stringify(inputSchema));

  /**
   * Step 2: Check if `unstract_key` exists in the `properties`.
   *         If not present, return the original schema without making any changes.
   */
  if (!schema.properties || !schema.properties.unstract_key) {
    return inputSchema; // Return the unmodified schema
  }

  /**
   * Step 3: Update the `required` array:
   *   - Remove `unstract_key` if it exists.
   *   - Ensure `use_platform_provided_unstract_key` is added to the array.
   */
  if (!Array.isArray(schema.required)) {
    schema.required = [];
  } else {
    // Remove `unstract_key` from the required array if it exists
    schema.required = schema.required.filter((req) => req !== "unstract_key");
  }

  // Add `use_platform_provided_unstract_key` to the required array if not already present
  if (!schema.required.includes("use_platform_provided_unstract_key")) {
    schema.required.push("use_platform_provided_unstract_key");
  }

  /**
   * Step 4: Mark `unstract_key` as `readOnly` by default.
   */
  schema.properties.unstract_key.readOnly = true;

  /**
   * Step 5: Define the new `use_platform_provided_unstract_key` property with the required details.
   */
  const usePlatformProvidedUnstractKeyProp = {
    type: "boolean",
    title: "Use Platform-Provided Unstract Key",
    default: false,
    description:
      "Choose whether to use the Unstract key automatically provided through your platform subscription, or manually enter your own LLM Whisperer key. Enable this option to use the platform-managed key.",
  };

  // Add or update the `use_platform_provided_unstract_key` property in the schema
  schema.properties.use_platform_provided_unstract_key =
    usePlatformProvidedUnstractKeyProp;

  /**
   * Step 6: Reorder the properties so that `use_platform_provided_unstract_key`
   *         appears immediately above `unstract_key`.
   */
  const newProperties = {};
  const propKeys = Object.keys(schema.properties);

  // Iterate through the keys to reorder
  for (const key of propKeys) {
    // Insert `use_platform_provided_unstract_key` immediately before `unstract_key`
    if (
      key === "unstract_key" &&
      !newProperties.use_platform_provided_unstract_key
    ) {
      newProperties.use_platform_provided_unstract_key =
        schema.properties.use_platform_provided_unstract_key;
    }
    // Add the current key (skipping `use_platform_provided_unstract_key` if already inserted)
    if (key !== "use_platform_provided_unstract_key") {
      newProperties[key] = schema.properties[key];
    }
  }

  // Update the properties with the reordered structure
  schema.properties = newProperties;

  /**
   * Step 7: Add or override dependencies for `use_platform_provided_unstract_key`.
   *         This dependency controls the `readOnly` state of `unstract_key` based on the value of `use_platform_provided_unstract_key`.
   */
  schema.dependencies = schema.dependencies || {};
  schema.dependencies.use_platform_provided_unstract_key = {
    oneOf: [
      {
        properties: {
          use_platform_provided_unstract_key: { const: false },
          unstract_key: { readOnly: false },
          url: { readOnly: false },
        },
        required: ["unstract_key"],
      },
      {
        properties: {
          use_platform_provided_unstract_key: { const: true },
          unstract_key: { readOnly: true },
          url: { readOnly: true },
        },
      },
    ],
  };

  /**
   * Step 8: Return the updated schema with all the required modifications.
   */
  return schema;
}

const LLMW_V2_ID = "llmwhisperer|a5e6b8af-3e1f-4a80-b006-d017e8e67f93";

export { transformLlmWhispererJsonSchema, LLMW_V2_ID };
